import { apiClient, apiFetchWithMeta } from '@/lib/api-client';
import type { Subscription } from '@/types/api';

export interface SubscriptionListFilters {
  status?: string | undefined;
  customerId?: string | undefined;
  cursor?: string | undefined;
}

/**
 * Superadmin subscription management.
 *
 * Every reason field is optional to the API but offered in the UI on purpose: months later,
 * "why is this customer suspended" is only answerable if whoever suspended them wrote it down.
 */
export const adminSubscriptionsApi = {
  list: (filters: SubscriptionListFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.customerId) params.set('customer_id', filters.customerId);
    if (filters.cursor) params.set('cursor', filters.cursor);

    const query = params.toString();
    return apiFetchWithMeta<Subscription[]>(`/admin/subscriptions${query ? `?${query}` : ''}`);
  },

  assign: (customerId: string, planId: string, days: number | null, reason: string) =>
    apiClient.post<Subscription>('/admin/subscriptions', {
      customer_id: customerId,
      plan_id: planId,
      ...(days !== null ? { days } : {}),
      reason,
    }),

  renew: (id: string, reason: string) =>
    apiClient.post<Subscription>(`/admin/subscriptions/${id}/renew`, { reason }),

  extend: (id: string, days: number, reason: string) =>
    apiClient.post<Subscription>(`/admin/subscriptions/${id}/extend`, { days, reason }),

  suspend: (id: string, reason: string) =>
    apiClient.post<Subscription>(`/admin/subscriptions/${id}/suspend`, { reason }),

  resume: (id: string, reason: string) =>
    apiClient.post<Subscription>(`/admin/subscriptions/${id}/resume`, { reason }),

  cancel: (id: string, reason: string, immediately: boolean) =>
    apiClient.post<Subscription>(`/admin/subscriptions/${id}/cancel`, { reason, immediately }),

  changePlan: (id: string, planId: string, reason: string) =>
    apiClient.post<Subscription>(`/admin/subscriptions/${id}/plan`, { plan_id: planId, reason }),
};
